import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import {
  PageMovementMode,
  PageMovementRow,
} from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { PROJECTS, QUESTIONS } from '../../../../shared/routes/path';
import ModelIconsList from './ModelIconsList';
import '../../../../shared/components/page-type-tag/PageTypeTag.scss';

interface PageMovementTableProps {
  rows: PageMovementRow[];
  mode: PageMovementMode;
  projectId: number;
}

// Short relative-time formatter — collapses to a single unit with a 1–2 char suffix
// (e.g. "3d", "2w", "2mo", "1y"). The previous version used date-fns `formatDistance`
// which produced "3 days ago" / "about 2 months ago" — too long for narrow date columns
// where the user just wants a glanceable freshness signal.
const formatShortDistance = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return '0s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(days / 365);
  return `${years}y`;
};

const PageMovementTable: React.FC<PageMovementTableProps> = ({ rows, mode, projectId }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();

  // Top rank — globally ordered by Usage count DESC (backend sort). The accumulator on
  // PageMovementSection appends sequential pages in order, so the row's array index + 1
  // is its true global rank within the current mode.
  const renderRank = (_row: PageMovementRow, options: { rowIndex: number }) => (
    <span className="page-movement-table__rank">{options.rowIndex + 1}</span>
  );

  // Renders the URL as path + query + hash only (drop scheme/host) — section is already
  // scoped to one domain so showing the host on every row adds noise. The text itself is
  // NOT a link; a separate `ico--external-link` icon next to it opens the full URL in a
  // new tab, matching the Top Citation Pages (SourcesStatsTable) pattern.
  const renderUrl = (row: PageMovementRow) => {
    let pathOnly = row.source;
    try {
      const u = new URL(row.source);
      pathOnly = `${u.pathname}${u.search}${u.hash}` || '/';
    } catch {
      // ignore — fall back to raw source string
    }
    return (
      <div className="page-movement-table__url">
        <span
          className="page-movement-table__url-text text-ellipsis"
          title={row.title?.trim() || row.source}
        >
          {pathOnly}
        </span>
        <a
          className="page-movement-table__external-link"
          href={row.source}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label={t('openInNewTab')}
        >
          <span className="ico--sm ico--external-link" />
        </a>
      </div>
    );
  };

  const renderModels = (row: PageMovementRow) => (
    <ModelIconsList models={row.models} tooltipKey={row.source} />
  );

  const renderFirstSeen = (row: PageMovementRow) =>
    row.firstSeenEver ? formatShortDistance(row.firstSeenEver) : '—';

  const renderLastUsed = (row: PageMovementRow) =>
    row.lastUsedInPeriod ? formatShortDistance(row.lastUsedInPeriod) : '—';

  // Usage count column — primary number (currentCount in current period) + inline tag with
  // the change vs the previous period. The tag is shown for top, topGainers, and topDecliners.
  // It is suppressed for newlyCited (where delta = currentCount and would duplicate the primary
  // number) and droppedOff (where the primary number is already previousCount, so a
  // `-previousCount` tag would just duplicate it). In `top`, brand-new URLs have delta = null
  // (no previous period to compare) so they also render without a tag.
  const renderUsageCount = (row: PageMovementRow) => {
    // For droppedOff, currentCount is always 0; show previousCount as the "usage count"
    // since that's the number that actually mattered for these URLs.
    const primaryCount = mode === 'droppedOff' ? row.previousCount : row.currentCount;

    let deltaTag: React.ReactNode = null;
    const showDelta =
      mode === 'topGainers' ||
      mode === 'topDecliners' ||
      (mode === 'top' && row.delta !== null);
    if (showDelta) {
      const value = row.delta ?? 0;
      const iconClass =
        value > 0 ? 'ico--chart-up' : value < 0 ? 'ico--chart-down' : 'ico--dot';
      const valueText = value > 0 ? `+${value}` : String(value);
      deltaTag = (
        <div className="tag tag--stats tag--md">
          <span className={iconClass} />
          <span className="tag__value">{valueText}</span>
        </div>
      );
    }

    return (
      <div className="page-movement-table__usage">
        <span className="page-movement-table__usage-count">{primaryCount}</span>
        {deltaTag}
      </div>
    );
  };

  // Mirrors the rowExpansionTemplate pattern from DomainSourcesTable: each prompt is a
  // clickable card navigating to the prompt stats page with chatDate + chatId so the
  // Stats accordion opens directly on the relevant chat response.
  const rowExpansionTemplate = (row: PageMovementRow) => {
    if (!row.prompts || row.prompts.length === 0) {
      return (
        <div className="page-movement-table__expansion page-movement-table__expansion--empty">
          {t('noPromptsForSource')}
        </div>
      );
    }
    return (
      <div className="page-movement-table__expansion">
        <div className="page-movement-table__expansion-title">{t('promptsCitingThisPage')}</div>
        {row.prompts.map((prompt) => {
          const chatDate = prompt.createdAt
            ? format(new Date(prompt.createdAt), 'yyyy-MM-dd')
            : '';
          return (
            <a
              key={`${prompt.monitorPromptId}-${prompt.monitorDataId}`}
              className="page-movement-table__prompt-box page-movement-table__prompt-box--clickable"
              onClick={() => {
                navigate(
                  `${PROJECTS}/${projectId}${QUESTIONS}/${prompt.monitorPromptId}/daily/all?chatDate=${chatDate}&chatId=${prompt.monitorDataId}`
                );
              }}
            >
              <div className="page-movement-table__prompt-content">
                <span className="page-movement-table__prompt-keyword">{prompt.keyword}</span>
                {prompt.snippet && (
                  <span className="page-movement-table__prompt-snippet">{prompt.snippet}</span>
                )}
              </div>
              <span className="page-movement-table__prompt-date">{chatDate}</span>
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <DataTable
      value={rows}
      dataKey="source"
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data as Record<string, boolean>)}
      rowExpansionTemplate={rowExpansionTemplate}
      stripedRows
      tableClassName="table"
      className="table-wrapper page-movement-table"
    >
      <Column header={t('top')} body={renderRank} style={{ width: '60px' }} />
      <Column header={t('url')} body={renderUrl} />
      <Column header={t('usageCount')} body={renderUsageCount} style={{ width: '140px' }} />
      <Column header={t('models')} body={renderModels} style={{ width: '120px' }} />
      <Column header={t('firstSeen')} body={renderFirstSeen} style={{ width: '90px' }} />
      <Column header={t('lastUsed')} body={renderLastUsed} style={{ width: '90px' }} />
      <Column expander style={{ width: '3rem' }} />
    </DataTable>
  );
};

export default PageMovementTable;